import { Link } from 'react-router-dom';
import { Leaf, Truck, CheckCircle2, TrendingUp, ShieldCheck, CalendarClock, PackageCheck, Users, Sprout, ArrowRight } from 'lucide-react';

const benefits = [
  { icon: Users, title: 'Reach more local customers', text: 'Tap into a growing base of White Mountain households looking for local food.' },
  { icon: Truck, title: 'No home delivery to manage', text: 'FarmBox handles pickup, packing, customer service, and last-mile delivery.' },
  { icon: PackageCheck, title: 'Confirmed quantities first', text: 'Customers order and pay before pickup, so you prepare only what is sold.' },
  { icon: TrendingUp, title: 'An additional sales channel', text: 'Sell through FarmBox alongside your existing markets and wholesale.' },
  { icon: ShieldCheck, title: 'You keep control', text: 'Set availability, propose pricing, and choose what you supply each week.' },
  { icon: CalendarClock, title: 'Seasonal producers welcome', text: 'No requirement to supply every product every week.' },
];

export default function ForFarms() {
  return (
    <div className="pt-20 bg-cream" data-testid="for-farms-page">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="container-custom py-20 md:py-28 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <span className="badge-local" data-testid="for-farms-eyebrow">For Farms &amp; Producers</span>
            <h1 className="font-playfair text-4xl md:text-6xl font-bold text-dark tracking-tight leading-[1.05] mt-6">
              Sell More Local Products Without Handling Home Delivery
            </h1>
            <p className="font-manrope text-lg text-dark/70 leading-relaxed mt-6 max-w-2xl">
              FarmBox connects New Hampshire farms and local producers with customers looking for
              convenient access to local food. Customers order and pay through FarmBox first. You
              receive confirmed quantities, prepare only what has been ordered, and FarmBox handles
              pickup, customer service, packing, and home delivery.
            </p>
            <div className="flex flex-wrap gap-4 mt-10">
              <Link to="/farm/apply" className="btn-primary" data-testid="apply-cta">
                Apply to Become a FarmBox Partner
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
              <Link to="/farm/login" className="btn-secondary" data-testid="farm-login-cta">
                Farm Partner Login
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative rounded-3xl overflow-hidden shadow-hover">
              <img
                src="https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=900"
                alt="Local farm field"
                className="w-full h-[420px] object-cover"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-dark/70 backdrop-blur p-6">
                <p className="font-caveat text-2xl text-gold">Grown here. Sold here.</p>
                <p className="font-manrope text-cream/90 text-sm mt-1">Join the local FarmBox supplier network.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="container-custom pb-8">
        <h2 className="font-playfair text-3xl md:text-4xl font-bold text-dark mb-3">Why partner with FarmBox</h2>
        <p className="font-manrope text-dark/60 mb-12 max-w-2xl">Everything you need to reach more customers, minus the logistics headache.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {benefits.map((b, i) => (
            <div key={i} className="bg-white rounded-2xl p-8 border border-forest/10 shadow-card hover:shadow-hover transition-shadow duration-500" data-testid={`benefit-${i}`}>
              <div className="w-12 h-12 rounded-full bg-forest/10 flex items-center justify-center mb-5">
                <b.icon className="w-6 h-6 text-forest" />
              </div>
              <h3 className="font-playfair text-xl font-bold text-dark mb-2">{b.title}</h3>
              <p className="font-manrope text-dark/60 leading-relaxed">{b.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="container-custom py-20">
        <div className="bg-dark rounded-3xl p-10 md:p-16 relative overflow-hidden noise-overlay">
          <h2 className="font-playfair text-3xl md:text-4xl font-bold text-cream mb-12">How the FarmBox partnership works</h2>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { icon: Sprout, t: 'Apply', d: 'Submit your farm application online.' },
              { icon: CheckCircle2, t: 'Get approved', d: 'FarmBox reviews and approves your farm.' },
              { icon: PackageCheck, t: 'Publish availability', d: 'List products and weekly quantities.' },
              { icon: Truck, t: 'Get paid', d: 'FarmBox picks up confirmed orders and pays you.' },
            ].map((s, i) => (
              <div key={i} className="relative">
                <div className="w-14 h-14 rounded-full bg-gold flex items-center justify-center mb-4">
                  <s.icon className="w-7 h-7 text-dark" />
                </div>
                <div className="font-caveat text-gold text-xl mb-1">Step {i + 1}</div>
                <h3 className="font-playfair text-xl font-bold text-cream mb-2">{s.t}</h3>
                <p className="font-manrope text-cream/70">{s.d}</p>
              </div>
            ))}
          </div>
          <div className="mt-12">
            <Link to="/farm/apply" className="btn-accent" data-testid="apply-cta-bottom">
              Apply to Become a FarmBox Partner
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
